import Link from "@components/Link/Link";
import { Paragraph } from "@components/ui/Paragraph/Paragraph";
import type { ReactNode } from "react";

import { useMenuStore } from "@/stores/menuStore";

import { ContentfulPicture } from "@app/_components/ContentfulPicture";
import { ContentfulVideo } from "@app/_components/ContentfulVideo";
import type { NavigationSplash } from "../types";

const Wrapper = ({
  slug,
  children,
  locale,
  className,
}: {
  slug?: string | null;
  children: ReactNode;
  locale?: string;
  className?: string;
}) => {
  const { clearActiveWithDelay } = useMenuStore();

  return slug ? (
    <Link
      locale={locale}
      onClick={() => clearActiveWithDelay()}
      href={slug}
      className={className}
      prefetch={false}
      customPrefetch="onMouseEnter"
    >
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );
};

type HighLightProps = {
  item?: NavigationSplash;
  locale?: string;
};

const DropdownHighlight = ({ item, locale }: HighLightProps) => {
  if (!item) {
    return null;
  }

  const {
    desktopImage,
    mobileImage,
    desktopVideo,
    mobileVideo,
    slug,
    title,
    content,
  } = item;

  if (!desktopImage && !mobileImage && !desktopVideo && !mobileVideo) {
    return null;
  }

  return (
    <Wrapper
      slug={slug}
      locale={locale}
      className="relative block max-w-full space-y-2"
    >
      <div className="relative aspect-square w-full overflow-hidden">
        <ContentfulPicture
          className="absolute inset-0 h-full w-full"
          mobileImage={mobileImage}
          desktopImage={desktopImage}
          imageProps={{
            sizes: "18em",
            className: "absolute inset-0 h-full w-full",
          }}
        />
        <ContentfulVideo
          desktopVideo={desktopVideo}
          mobileVideo={mobileVideo}
        />
      </div>
      {title && (
        <Paragraph className="all-small-caps mb-2 text-base font-normal tracking-wider uppercase">
          {title}
        </Paragraph>
      )}
      {content && (
        <Paragraph className="mt-1.5 text-base font-normal">
          {content}
        </Paragraph>
      )}
    </Wrapper>
  );
};

export default DropdownHighlight;
